import {
    Form,
    redirect,
    LoaderFunctionArgs,
    ActionFunctionArgs,
    useLoaderData,
    Link,
    useNavigate,
} from "react-router-dom";
import toast from "react-hot-toast";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import {Input, TextArea} from "../../components/inputs/Input";
import CustomSelect from "../../components/customSelect";
import {FormLayout} from "../../layout/FormLayout";
import {createApolloClient} from "../../providers/ApolloClientFactory";
import {
    GET_LIST_ATTENDEES_BY_ORGANIZATION,
    GET_LIST_EVENTS_BY_ORGANIZATION,
} from "../../queries";
import {organizationStore} from "../../store/organization";
import {Attendee} from "../../types/attendee";
import {Event} from "../../types/event";
import {RFID} from "../../types/rfid";
import {UPDATE_RFID_BY_ID, UPDATE_RFID_BY_ID_WITHOUT_PIN, UPDATE_RFID_TOKENS_ONLY, UPDATE_RFID_BALANCES, UPDATE_TRANSFER_STATUS} from "./mutations";
import {RFID_BY_ID_QUERY, RFID_CASH_BALANCE_HISTORY, RFID_PROMO_BALANCE_HISTORY, RFID_TRANSFER_HISTORY, RFID_BY_UID_QUERY_EXPANDED} from "./queries";
import {Badge} from "../../components/Badge";
import {TertiaryButton, PrimaryButton, SecondaryButton} from "../../components/Button";
import PriceInput from "../../components/inputs/PriceInput";
import React, {useEffect, useRef, useState} from "react";
import {userStore} from "../../store/user";
import {CashBalHistoryRow, PromoBalHistoryRow} from "../attendees/components/type";
import {ShowLayout} from "../../layout/ShowLayout";
import Loader from "../../components/Loader";
import {AgGridReact} from "ag-grid-react";
import {GridApi, SizeColumnsToContentStrategy} from "ag-grid-community";
import {cashBalHistoryColDefs, promoBalHistoryColDefs, generateTransferHistoryColDefs, TransferHistoryRow} from "./rfidHelpers";
import ReverseTransferModal from "./ReverseTransferModal";

const client = createApolloClient();

const {getState} = organizationStore;
const {getState: getUserStore} = userStore;

interface TokenBalance {
    balance: number;
    redeemable_token_id: string;
    redeemable_token_name: string;
    /** Variant tickets ("Hotel Route 1 | Hilton | Friday"): the base ticket name a gate may accept instead. */
    redeemable_token_base?: string;
}

type TokenBalances = {
    [key: string]: TokenBalance;
};

type Token = {
    name: string;
    balance: number;
};
export const editRfidAction = async ({
                                         request,
                                         params,
                                     }: ActionFunctionArgs) => {
    const body = await request.formData();
    const { id } = params;
    const cashBalanceString = body.get("cash_balance") as string;
    const cashBalance = parseFloat(
        cashBalanceString.length ? cashBalanceString : "0"
    );
    const promoBalanceString = body.get("promo_balance") as string;
    const promoBalance = parseFloat(
        promoBalanceString.length ? promoBalanceString : "0"
    );
    const updatedTokenBalances = JSON.parse(body.get("token_balances") as string);
    const user = getUserStore().user;

    if (body.get("pinUpdated") === "true") {
        try {
            await client.mutate({
                mutation: UPDATE_RFID_BY_ID,
                variables: {
                    id: parseInt(id as string),
                    uid: body.get("uid"),
                    is_active: body.get("is_active"),
                    event_id: body.get("event_id"),
                    last_four_phone_numbers: body.get("last_four_phone_numbers"),
                    personnal_pin: body.get("last_four_phone_numbers"),
                    reg_id: body.get("reg_id"),
                    access_type: body.get("access_type"),
                    cash_balance: Math.round(cashBalance * 100),
                    promo_balance: Math.round(promoBalance * 100),
                    tokens_balance: updatedTokenBalances,
                    notes: body.get("notes"),
                    last_updated_by: user?.id,
                },
            });
            toast.success("RFID Asset updated successfully");
            return redirect("/rfid-assets/list");
        } catch {
            toast.error("Error updating RFID Asset");
        }
    } else {
        try {
            await client.mutate({
                mutation: UPDATE_RFID_BY_ID_WITHOUT_PIN,
                variables: {
                    id: parseInt(id as string),
                    uid: body.get("uid"),
                    is_active: body.get("is_active"),
                    event_id: body.get("event_id"),
                    reg_id: body.get("reg_id"),
                    access_type: body.get("access_type"),
                    cash_balance: Math.round(cashBalance * 100),
                    promo_balance: Math.round(promoBalance * 100),
                    tokens_balance: updatedTokenBalances,
                    notes: body.get("notes"),
                    last_updated_by: user?.id,
                },
            });
            toast.success("RFID Asset updated successfully");
            return redirect("/rfid-assets/list");
        } catch {
            toast.error("Error updating RFID Asset");
        }
    }
};

export const editRFIDLoader = async ({params}: LoaderFunctionArgs) => {
    const organizationId = getState().organizationId;
    const {id} = params;
    const user = getUserStore().user;

    let where: any = {
        organization_id: {_eq: organizationId},
    };
    if (user?.events) {
        where = {
            ...where,
            id: {
                _in: user?.events,
            },
        };
    }
    const [{data: rfid}, {data: attendeesData}, {data: eventsData}] =
        await Promise.all([
            client.query({
                query: RFID_BY_ID_QUERY,
                variables: {
                    id,
                },
            }),
            client.query({
                query: GET_LIST_ATTENDEES_BY_ORGANIZATION,
                variables: {
                    organizationId,
                },
            }),
            client.query({
                query: GET_LIST_EVENTS_BY_ORGANIZATION,
                variables: {
                    where,
                },
            }),
            client.query({
                query: GET_LIST_EVENTS_BY_ORGANIZATION,
                variables: {
                    where,
                },
            }),
        ]);
    return {
        rfid: rfid.rfid_assets[0],
        attendees: attendeesData.attendees,
        events: eventsData.events,
    };
};

export const RFIDAssetEdit = () => {
    const {rfid, attendees, events} = useLoaderData() as {
        rfid: RFID;
        attendees: Attendee[];
        events: Event[];
    };
    const [pinUpdate, setPinUpdate] = useState(false);
    const selectedAttendee = attendees.find((a) => a.id === rfid.attendee_id);
    const [cashBalance, setCashBalance] = useState(rfid.cash_balance / 100);
    const [promoBalance, setPromoBalance] = useState(rfid.promo_balance / 100);
    const [tokens, setTokens] = useState<Token[]>([]);
    const [cashBalHistory, setCashBalHistory] = useState<CashBalHistoryRow[]>([]);
    const [isLoadingCashBalHistory, setIsLoadingCashBalHistory] = useState(true);
    const [promoBalHistory, setPromoBalHistory] = useState<PromoBalHistoryRow[]>([]);
    const [isLoadingPromoBalHistory, setIsLoadingPromoBalHistory] = useState(true);
    const [transferHistory, setTransferHistory] = useState<TransferHistoryRow[]>([]);
    const [isLoadingTransferHistory, setIsLoadingTransferHistory] = useState(true);
    const [showReverseModal, setShowReverseModal] = useState(false);
    const [selectedTransfer, setSelectedTransfer] = useState<TransferHistoryRow | null>(null);
    const navigate = useNavigate();

    const handleClone = () => {
        navigate(`/rfid-assets/${rfid.id}/clone`);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [cashBalHistory] = await Promise.all([
                    client.query({
                        query: RFID_CASH_BALANCE_HISTORY,
                        variables: {
                            eventId: rfid?.event_id,
                            uid: rfid?.uid,
                        },
                    }),
                ]);
                setCashBalHistory(cashBalHistory.data.cash_balance_history);
            } catch (error) {
                toast.error("Error Fetching Card Data");
            } finally {
                setIsLoadingCashBalHistory(false);
            }
        };
        if (rfid) {
            fetchData();
        }
    }, [rfid]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [promoBalHistory] = await Promise.all([
                    client.query({
                        query: RFID_PROMO_BALANCE_HISTORY,
                        variables: {
                            eventId: rfid?.event_id,
                            uid: rfid?.uid,
                        },
                    }),
                ]);
                setPromoBalHistory(promoBalHistory.data.promo_balance_history);
            } catch (error) {
                toast.error("Error Fetching Card Data");
            } finally {
                setIsLoadingPromoBalHistory(false);
            }
        };
        if (rfid) {
            fetchData();
        }
    }, [rfid]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [transferHistoryData] = await Promise.all([
                    client.query({
                        query: RFID_TRANSFER_HISTORY,
                        variables: {
                            eventId: rfid?.event_id,
                            uid: rfid?.uid,
                        },
                    }),
                ]);
                // Add transfer type to each row based on whether this UID was the sender or receiver
                const enrichedHistory = transferHistoryData.data.cashless_asset_transfer_history.map((transfer: TransferHistoryRow) => ({
                    ...transfer,
                    transferType: String(transfer.source_uid) === String(rfid.uid) ? 'Sent' : 'Received'
                }));
                setTransferHistory(enrichedHistory);
            } catch (error) {
                toast.error("Error Fetching Transfer History");
            } finally {
                setIsLoadingTransferHistory(false);
            }
        };
        if (rfid) {
            fetchData();
        }
    }, [rfid]);

    useEffect(() => {
        if (rfid.tokens_balance) {
            // Convert tokens_balance object to array format
            const tokensArray = Object.entries(rfid.tokens_balance as TokenBalances).map(([key, value]: [string, TokenBalance]) => ({
                name: key,
                balance: value.balance || 0,
            }));
            setTokens(tokensArray.length > 0 ? tokensArray : [{ name: "", balance: 0 }]);
        } else {
            setTokens([{ name: "", balance: 0 }]);
        }
    }, [rfid.tokens_balance]);

    const handleAddToken = () => {
        setTokens([...tokens, { name: "", balance: 0 }]);
    };

    const handleRemoveToken = (index: number) => {
        if (tokens.length > 1) {
            setTokens(tokens.filter((_, i) => i !== index));
        }
    };

    const handleTokenChange = (
        index: number,
        field: keyof Token,
        value: string | number | undefined
    ) => {
        const updatedTokens = [...tokens];
        if (field === "balance") {
            updatedTokens[index].balance = Number(value) || 0;
        } else {
            updatedTokens[index].name = String(value);
        }
        setTokens(updatedTokens);
    };

    // Convert tokens array back to the proper object format for submission.
    // The form only edits name and balance; keep whatever else the stored
    // entry carries (redeemable_token_base on variant tickets), otherwise a
    // save here strips it and the gate that lists the base name stops
    // accepting the ticket.
    const getTokenBalancesObject = () => {
        const stored = (rfid.tokens_balance as TokenBalances | null | undefined) ?? {};
        const tokenBalances: TokenBalances = {};
        tokens.forEach((token) => {
            if (token.name.trim() !== "") {
                tokenBalances[token.name] = {
                    ...(stored[token.name] ?? {}),
                    balance: token.balance,
                    redeemable_token_id: token.name,
                    redeemable_token_name: token.name,
                };
            }
        });
        return tokenBalances;
    };

    const handleCashBalanceChange = (value: number) => {
        setCashBalance(value);
    };

    const handlePromoBalanceChange = (value: number) => {
        setPromoBalance(value);
    };

    const handleReverseClick = (transfer: TransferHistoryRow) => {
        setSelectedTransfer(transfer);
        setShowReverseModal(true);
    };

    const handleReverseConfirm = async (
        transferId: number,
        sourceUid: string,
        destinationUid: string,
        reversalDetails: any
    ) => {
        try {
            // Fetch current RFID assets
            const [sourceData, destData] = await Promise.all([
                client.query({
                    query: RFID_BY_UID_QUERY_EXPANDED,
                    variables: { uid: sourceUid },
                }),
                client.query({
                    query: RFID_BY_UID_QUERY_EXPANDED,
                    variables: { uid: destinationUid },
                }),
            ]);

            const sourceAsset = sourceData.data.rfid_assets[0];
            const destAsset = destData.data.rfid_assets[0];

            if (!sourceAsset || !destAsset) {
                throw new Error("Could not find source or destination asset");
            }

            // Calculate new balances
            const sourceCurrentTokens = sourceAsset.tokens_balance || {};
            const destCurrentTokens = destAsset.tokens_balance || {};

            const newSourceTokens = { ...sourceCurrentTokens };
            const newDestTokens = { ...destCurrentTokens };

            // Add tokens back to source and remove from destination
            Object.entries(reversalDetails.tokensToAdd).forEach(([tokenId, tokenData]: [string, any]) => {
                if (newSourceTokens[tokenId]) {
                    newSourceTokens[tokenId] = {
                        ...newSourceTokens[tokenId],
                        balance: (newSourceTokens[tokenId].balance || 0) + tokenData.balance,
                    };
                } else {
                    newSourceTokens[tokenId] = tokenData;
                }
            });

            Object.entries(reversalDetails.tokensToRemove).forEach(([tokenId, tokenData]: [string, any]) => {
                if (newDestTokens[tokenId]) {
                    newDestTokens[tokenId] = {
                        ...newDestTokens[tokenId],
                        balance: Math.max(0, (newDestTokens[tokenId].balance || 0) - tokenData.balance),
                    };
                }
            });

            // Calculate new cash and promo balances
            const newSourceCashBalance = reversalDetails.sourceAfterCashBalance;
            const newDestCashBalance = reversalDetails.destAfterCashBalance;
            const newSourcePromoBalance = reversalDetails.sourceAfterPromoBalance;
            const newDestPromoBalance = reversalDetails.destAfterPromoBalance;

            // Execute mutations
            await Promise.all([
                client.mutate({
                    mutation: UPDATE_RFID_BALANCES,
                    variables: {
                        uid: sourceUid,
                        event_id: sourceAsset.event_id,
                        tokens_balance: newSourceTokens,
                        cash_balance: newSourceCashBalance,
                        promo_balance: newSourcePromoBalance,
                    },
                }),
                client.mutate({
                    mutation: UPDATE_RFID_BALANCES,
                    variables: {
                        uid: destinationUid,
                        event_id: destAsset.event_id,
                        tokens_balance: newDestTokens,
                        cash_balance: newDestCashBalance,
                        promo_balance: newDestPromoBalance,
                    },
                }),
                client.mutate({
                    mutation: UPDATE_TRANSFER_STATUS,
                    variables: {
                        id: transferId,
                        status: "Reversed",
                    },
                }),
            ]);

            // Refresh transfer history
            const transferHistoryData = await client.query({
                query: RFID_TRANSFER_HISTORY,
                variables: {
                    eventId: rfid?.event_id,
                    uid: rfid?.uid,
                },
                fetchPolicy: 'network-only',
            });

            const enrichedHistory = transferHistoryData.data.cashless_asset_transfer_history.map((transfer: TransferHistoryRow) => ({
                ...transfer,
                transferType: String(transfer.source_uid) === String(rfid.uid) ? 'Sent' : 'Received'
            }));
            setTransferHistory(enrichedHistory);

            setShowReverseModal(false);
            setSelectedTransfer(null);
        } catch (error) {
            console.error("Error reversing transfer:", error);
            throw error;
        }
    };

    const cashBalGridRef = useRef<GridApi>();
    const promoBalGridRef = useRef<GridApi>();
    const transferGridRef = useRef<GridApi>();
    const rowHeight = 50;
    const autoSizeStrategy: SizeColumnsToContentStrategy = {
        type: "fitCellContents",
    };
    const gridOptions = {
        enableCellTextSelection: true,
    };

    return (
        <div>
            <Form method="post" action={`/rfid-assets/${rfid.id}/edit`}>
                <div className="flex">
                    <div className="ml-auto py-2">
                        <TertiaryButton onClick={handleClone}>Clone RFID Asset</TertiaryButton>
                    </div>
                </div>
                <FormLayout>
                    <div className="col-span-6 sm:col-span-1">
                        <Input label="UID" name="uid" defaultValue={rfid.uid} readOnly/>
                    </div>
                    <div className="col-span-6 sm:col-span-5">
                        <IndeterminateCheckbox
                            label="First Association Complete?"
                            name="is_active"
                            defaultChecked={rfid.is_active}
                        />
                    </div>
                    <div className="col-span-6 sm:col-span-6">
                        <p className="block text-sm font-medium text-gray-700">Attendee:</p>
                        <p className="block text-sm font-small text-gray-700">
                            <Link to={`/attendees/${rfid.attendee_id}/edit`}>
                                <Badge>
                                    {selectedAttendee ? selectedAttendee.first_name : ""}{" "}
                                    {selectedAttendee ? selectedAttendee.last_name : ""}
                                </Badge>
                            </Link>
                        </p>
                    </div>
                    <div className="col-span-6 sm:col-span-6">
                        <CustomSelect
                            title="Event"
                            name="event_id"
                            defaultValue={rfid.event_id}
                            options={events.map((e) => ({
                                label: e.name,
                                value: e.id,
                            }))}
                        />
                    </div>
                    <div className="col-span-1">
                        <IndeterminateCheckbox
                            label="Update PIN?"
                            name="pinUpdated"
                            defaultChecked={false}
                            onChange={(checked) => {
                                setPinUpdate(checked as boolean);
                            }}
                        />
                    </div>
                    {pinUpdate && (
                        <div className="col-span-5 sm:col-span-5">
                            <Input label="PIN" name="last_four_phone_numbers" required/>
                        </div>
                    )}
                    <div className="col-span-6 sm:col-span-6">
                        <Input label="Print ID" name="reg_id" defaultValue={rfid.reg_id}/>
                    </div>
                    <div className="col-span-6 sm:col-span-6">
                        <Input
                            label="Access Type"
                            name="access_type"
                            defaultValue={rfid.access_type}
                        />
                    </div>
                    <div className="col-span-6 sm:col-span-6">
                        <PriceInput
                            label="Cash Balance"
                            name="cash_balance"
                            value={cashBalance}
                            onChange={(value) => handleCashBalanceChange(value ? value : 0)}
                        />
                    </div>
                    <div className="col-span-6">
                        <PriceInput
                            label="Promo Balance"
                            name="promo_balance"
                            value={promoBalance}
                            onChange={(value) => handlePromoBalanceChange(value ? value : 0)}
                        />
                    </div>
                    <div className="col-span-6">
                        <p className="block text-sm font-medium text-gray-700 mb-3">
                            Token Balances:
                        </p>
                        <div className="space-y-3">
                            {tokens.map((token, index) => (
                                <div key={index} className="grid grid-cols-12 gap-4">
                                    <div className="col-span-6">
                                        <Input
                                            label="Token Name"
                                            name={`tokens[${index}][name]`}
                                            value={token.name}
                                            onChange={(value) => handleTokenChange(index, "name", String(value))}
                                        />
                                    </div>
                                    <div className="col-span-4">
                                        <Input
                                            label="Balance"
                                            name={`tokens[${index}][balance]`}
                                            value={token.balance}
                                            type="number"
                                            onChange={(value) => handleTokenChange(index, "balance", Number(value) || 0)}
                                        />
                                    </div>
                                    <div className="col-span-2 flex items-end">
                                        {tokens.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveToken(index)}
                                                className="mb-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div className="mt-2">
                                <PrimaryButton type="button" onClick={handleAddToken}>
                                    + Add Token
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                    <div className="col-span-6">
                        <TextArea label="Notes" name="notes" defaultValue={rfid.notes}/>
                    </div>
                    <input
                        type="hidden"
                        name="token_balances"
                        value={JSON.stringify(getTokenBalancesObject())}
                    />
                </FormLayout>
            </Form>
            <div className="mt-2 col-span-6">
                <ShowLayout
                    header="Cash Balance History"
                    actions={
                        <SecondaryButton onClick={() => cashBalGridRef.current?.exportDataAsCsv({ fileName: "CashBalanceHistory.csv", columnSeparator: ",", allColumns: true })}>
                            Export to CSV
                        </SecondaryButton>
                    }
                >
                    <div style={{height: "25vh"}}>
                        {isLoadingCashBalHistory ? (
                            <Loader/>
                        ) : (
                            <div
                                className={"ag-theme-quartz"}
                                style={{width: "100%", height: "100%"}}
                            >
                                <AgGridReact
                                    rowData={cashBalHistory}
                                    columnDefs={cashBalHistoryColDefs}
                                    rowHeight={rowHeight}
                                    autoSizeStrategy={autoSizeStrategy}
                                    pagination={true}
                                    gridOptions={gridOptions}
                                    paginationPageSize={100}
                                    onGridReady={(params) => (cashBalGridRef.current = params.api)}
                                />
                            </div>
                        )}
                    </div>
                </ShowLayout>
            </div>
            <div className="mt-2 col-span-6">
                <ShowLayout
                    header="Promo Balance History"
                    actions={
                        <SecondaryButton onClick={() => promoBalGridRef.current?.exportDataAsCsv({ fileName: "PromoBalanceHistory.csv", columnSeparator: ",", allColumns: true })}>
                            Export to CSV
                        </SecondaryButton>
                    }
                >
                    <div style={{height: "25vh"}}>
                        {isLoadingPromoBalHistory ? (
                            <Loader/>
                        ) : (
                            <div
                                className={"ag-theme-quartz"}
                                style={{width: "100%", height: "100%"}}
                            >
                                <AgGridReact
                                    rowData={promoBalHistory}
                                    columnDefs={promoBalHistoryColDefs}
                                    rowHeight={rowHeight}
                                    autoSizeStrategy={autoSizeStrategy}
                                    pagination={true}
                                    gridOptions={gridOptions}
                                    paginationPageSize={100}
                                    onGridReady={(params) => (promoBalGridRef.current = params.api)}
                                />
                            </div>
                        )}
                    </div>
                </ShowLayout>
            </div>
            <div className="mt-2 col-span-6">
                <ShowLayout
                    header="Transfer History"
                    actions={
                        <SecondaryButton onClick={() => transferGridRef.current?.exportDataAsCsv({ fileName: "TransferHistory.csv", columnSeparator: ",", allColumns: true })}>
                            Export to CSV
                        </SecondaryButton>
                    }
                >
                    <div style={{height: "25vh"}}>
                        {isLoadingTransferHistory ? (
                            <Loader/>
                        ) : (
                            <div
                                className={"ag-theme-quartz"}
                                style={{width: "100%", height: "100%"}}
                            >
                                <AgGridReact
                                    rowData={transferHistory}
                                    columnDefs={generateTransferHistoryColDefs(handleReverseClick)}
                                    rowHeight={rowHeight}
                                    autoSizeStrategy={autoSizeStrategy}
                                    pagination={true}
                                    gridOptions={gridOptions}
                                    paginationPageSize={100}
                                    onGridReady={(params) => (transferGridRef.current = params.api)}
                                />
                            </div>
                        )}
                    </div>
                </ShowLayout>
            </div>

            <ReverseTransferModal
                isOpen={showReverseModal}
                onClose={() => {
                    setShowReverseModal(false);
                    setSelectedTransfer(null);
                }}
                onConfirm={handleReverseConfirm}
                transfer={selectedTransfer}
            />
        </div>
    );
};
